import { GrayImage3 } from './../image-slice-show/share/image';
import { View, CrossLine } from './../cross-line/cross-line.component';
import { Color, Vector2, Vector3, Shape, Texture } from 'three';
import { BoundingBox2 } from './share/boundingbox2';

export interface PlaneTextureData {
  r: Uint8Array;
  g: Uint8Array;
  b: Uint8Array;
  shape: Vector2;
}

export class Plane2Model {
  type: 'plane2' = 'plane2';
  constructor(
    public readonly box: BoundingBox2,
    public readonly z = 10,
    public readonly textureData?: PlaneTextureData,
    public readonly rotation: Vector3 = new Vector3(0, 0, 0),
    public readonly transparent: boolean = false,
    public readonly opacity: number = 1,
    public readonly wireFrame: boolean = false,
    public readonly color?: Color,
  ) {}
}

export class InfoPlanceModel {
  type: 'infoplane' = 'infoplane';
  constructor(
    public readonly box: BoundingBox2,
    public readonly z = 15,
    public readonly texture?: HTMLCanvasElement,
    public readonly transparent: boolean = false,
    public readonly opacity: number = 1,
    public readonly wireFrame: boolean = false,
  ) {}
}

export class CubeModel {
  type: 'cube' = 'cube';
  constructor(
    public readonly size: Vector3,
    public readonly center: Vector3,
    public readonly rotate: Vector3,
  ) {}
}

export class SphereModel {
  type: 'sphere' = 'sphere';
  constructor(
    public readonly center: Vector3,
    public readonly radius: number,
    public readonly widthSegments: number = 32,
    public readonly heightSegments: number = 32,
    public readonly phiStart: number = 0,
    public readonly phiLength: number = 2 * Math.PI,
    public readonly thetaStart: number = 0,
    public readonly thetaLength: number = 0,
  ) {}
}

export class CircleModel {
  type: 'circle' = 'circle';
  constructor(
    public readonly center: Vector3,
    public readonly radius: number,
    public readonly segments: number = 32,
    public readonly thetaStart: number = 0,
    public readonly thetaLength: number = 2 * Math.PI,
  ) {}
}
export class RingModel {
  type: 'ring' = 'ring';
  constructor(
    public readonly center: Vector3,
    public readonly innerRadius: number = 10,
    public readonly outerRadius: number = 50,
    public readonly thetaSegments: number = 32,
    public readonly phiSegments: number = 32,
    public readonly thetaStart: number = 0,
    public readonly thetaLength: number = 2 * Math.PI,
  ) {}
}
export class UserDefinedModel {
  type: 'user-defined' = 'user-defined';
  constructor(public readonly shapes: Vector2[] | undefined) {}
}
export class ProfileModel {
  type: 'profile1d' = 'profile1d';
  constructor(public readonly points: number[] | undefined) {}
}
export class LineModel {
  type: 'line' = 'line';
  constructor(
    public readonly start: Vector3,
    public readonly end: Vector3,
    public readonly style: 'Solid' | 'Dashed' = 'Solid',
    public readonly color: Color = new Color(0, 0.2, 1),
  ) {}
}

export class OBJModel {
  type: 'obj' = 'obj';
  constructor(public readonly center: Vector3) {}
}

export class VRMLModel {
  type: 'vrml' = 'vrml';
  constructor(public readonly center: Vector3) {}
}

export class CrossLineModel {
  type: 'crossline' = 'crossline';
  constructor(
    public readonly position: Vector3,
    public readonly view: View,
    public readonly lineWidth = 1.5,
  ) {}
}

export class FovModel {
  type: 'fov' = 'fov';
  constructor(public readonly box: BoundingBox2) {}
}

export class VolumeModel {
  type: 'volume' = 'volume';
  constructor(
    public readonly color: string = 'red', // default,spectrum,hot_and_cold,gold,red,green,blue,walking_dead,random,muscle_bone
    public readonly opacity: string = 'bandpass', // linear,lowpass,highpass,bandpass,flat,random,linear_full
    public readonly algorithm: number = 0, // 0,1
    public readonly alphaCorrection: number = 0.25, // 0-1
    public readonly volume: GrayImage3 = new GrayImage3(
      new Vector3(0, 0, 0),
      new Vector3(190, 256, 151),
      new Vector3(190, 256, 151),
      initArray(new Float32Array(190 * 256 * 151), 10),
    ),
  ) {}
}
function initArray(image: Float32Array, value: number) {
  const newArray = image.map((item, index, array) => value);
  return newArray;
}
export type EntityModel =
  | OBJModel
  | Plane2Model
  | CubeModel
  | LineModel
  | SphereModel
  | CircleModel
  | UserDefinedModel
  | RingModel
  | ProfileModel
  | VRMLModel
  | CrossLineModel
  | InfoPlanceModel
  | FovModel
  | VolumeModel;

export type ModelType = EntityModel['type'];
export type ModelOf<K extends ModelType> = Extract<EntityModel, { type: K }>;
